import * as THREE from 'three';

/**
 * Cuts one of the world's merged meshes into square tiles of the ground, one mesh per tile.
 *
 * The world is merged by material so that a city is a dozen draw calls rather than ten
 * thousand (DECISIONS.md §81). The cost of that, on a big region, is that every merged mesh
 * has a bounding sphere the size of the region: three.js's frustum culling can never reject
 * one, so every wall in a 4 km² city is drawn every frame, and drawn again into the shadow
 * map, whichever way the camera is facing and however little of the shadow box it falls in.
 *
 * Tiles give culling something to work with — behind the camera, outside the shadow box and
 * past the far plane all stop being drawn — and, because each tile carries its own origin,
 * they also give three.js's opaque sort something to sort by. Tiles near the camera are drawn
 * first, so the depth test rejects the walls hidden behind them before the facade shader
 * runs on them. Merged into one mesh with its origin at the world's centre, the sort could
 * not tell one street from another.
 *
 * Triangles go to the tile their centroid is in. A triangle that crosses a tile edge is not
 * cut; it only widens that tile's bounding sphere, which is the conservative direction.
 */
export interface Chunked {
  group: THREE.Group;
  geometries: THREE.BufferGeometry[];
}

export function chunkMesh(mesh: THREE.Mesh, size: number): Chunked {
  const source = mesh.geometry;
  if (source.index) throw new Error(`chunkMesh: ${mesh.name} is indexed`);
  const position = source.getAttribute('position');
  const triangles = position.count / 3;

  const tiles = new Map<string, number[]>();
  for (let t = 0; t < triangles; t++) {
    const a = t * 3;
    const x = (position.getX(a) + position.getX(a + 1) + position.getX(a + 2)) / 3;
    const z = (position.getZ(a) + position.getZ(a + 1) + position.getZ(a + 2)) / 3;
    const key = `${Math.floor(x / size)},${Math.floor(z / size)}`;
    const list = tiles.get(key);
    if (list) list.push(t);
    else tiles.set(key, [t]);
  }

  const group = new THREE.Group();
  group.name = mesh.name;
  group.matrixAutoUpdate = false;
  const geometries: THREE.BufferGeometry[] = [];
  const names = Object.keys(source.attributes);

  for (const tris of tiles.values()) {
    const geometry = new THREE.BufferGeometry();
    for (const name of names) {
      const attribute = source.getAttribute(name) as THREE.BufferAttribute;
      const item = attribute.itemSize;
      const from = attribute.array as Float32Array;
      const to = new Float32Array(tris.length * 3 * item);
      let o = 0;
      for (const t of tris) {
        const start = t * 3 * item;
        to.set(from.subarray(start, start + 3 * item), o);
        o += 3 * item;
      }
      geometry.setAttribute(name, new THREE.BufferAttribute(to, item));
    }

    // The tile's own origin, at the middle of what is in it, for the sort and the culling.
    const pos = geometry.getAttribute('position') as THREE.BufferAttribute;
    const array = pos.array as Float32Array;
    let minX = Infinity;
    let maxX = -Infinity;
    let minZ = Infinity;
    let maxZ = -Infinity;
    for (let i = 0; i < array.length; i += 3) {
      minX = Math.min(minX, array[i]);
      maxX = Math.max(maxX, array[i]);
      minZ = Math.min(minZ, array[i + 2]);
      maxZ = Math.max(maxZ, array[i + 2]);
    }
    const cx = (minX + maxX) / 2;
    const cz = (minZ + maxZ) / 2;
    for (let i = 0; i < array.length; i += 3) {
      array[i] -= cx;
      array[i + 2] -= cz;
    }
    geometry.computeBoundingSphere();

    const tile = new THREE.Mesh(geometry, mesh.material);
    tile.name = mesh.name;
    tile.position.set(cx, 0, cz);
    tile.castShadow = mesh.castShadow;
    tile.receiveShadow = mesh.receiveShadow;
    tile.renderOrder = mesh.renderOrder;
    tile.matrixAutoUpdate = false;
    tile.updateMatrix();
    group.add(tile);
    geometries.push(geometry);
  }
  group.updateMatrixWorld(true);
  return { group, geometries };
}
